/**
 * Keeps Sentry payloads free of PII, secrets, and oversized bodies.
 * Adapted from Cirenio sentry-error-reporting v1 (SentryContextSanitizer).
 */

export const ALLOWED_CONTEXT_KEYS = [
  'operation',
  'route',
  'provider',
  'attempt',
  'status',
  'index',
  'exception',
  'http_status',
  'step',
];

const SENSITIVE_EXACT_KEYS = [
  'query',
  'http.query',
  'query_string',
];

const SENSITIVE_KEY_FRAGMENTS = [
  'password',
  'passwd',
  'secret',
  'token',
  'authorization',
  'api_key',
  'apikey',
  'cookie',
  'cuil',
  'cuit',
  'dni',
  'email',
  'mail',
  'phone',
  'telefono',
  'nombre',
  'first_name',
  'firstname',
  'last_name',
  'lastname',
  'full_name',
  'fullname',
  'surname',
  'apellido',
  'client_name',
  'formdata',
  'mobileurl',
  'card_token',
  'cardtoken',
  'payload',
  'response',
  'request_body',
  'body',
  'binding',
  'bindings',
  'bearer',
  'private_key',
  'credit_card',
  'card_number',
  'cvv',
  'cbu',
  'cvu',
  'iban',
  'alias',
  'account_number',
  'pan',
  'tarjeta',
  'http.query',
  'query_string',
];

const RISKY_MESSAGE_PATTERNS = [
  'serialize(',
  'Bearer ',
  'cuil=',
  'cuit=',
  'dni=',
  'password=',
  'api_key=',
  'authorization:',
  '"dni"',
  "'dni'",
  '"cuil"',
  '"cuit"',
  '"email"',
  '"token"',
  '"firstName"',
  '"lastName"',
  '"formData"',
  '"cardNumber"',
  '"mobileUrl"',
  'formdata:',
];

const LONG_DIGIT_RUN = /\d{7,}/g;

const URL_FIELD_KEYS = [
  'from',
  'to',
  'url',
  'referer',
  'referrer',
  'location',
];

const MOBILE_TOKEN_PATH = /\/m\/(docs|onboarding|signature)\/[^/?#]+/gi;

export function filterContext(context) {
  if (!context || typeof context !== 'object') {
    return {};
  }

  const safe = {};

  Object.entries(context).forEach(([key, value]) => {
    if (!ALLOWED_CONTEXT_KEYS.includes(key) || isSensitiveKey(key)) {
      return;
    }

    if (typeof value === 'boolean' || typeof value === 'number' || value === null) {
      safe[key] = value;
      return;
    }

    if (typeof value === 'string') {
      safe[key] = truncate(value);
    }
  });

  return safe;
}

export function scrub(data, depth = 0) {
  if (depth > 8) {
    return '[max_depth]';
  }

  if (Array.isArray(data)) {
    return data.map((value) => scrub(value, depth + 1));
  }

  if (data && typeof data === 'object') {
    const scrubbed = {};
    Object.entries(data).forEach(([key, value]) => {
      if (isSensitiveKey(key)) {
        scrubbed[key] = '[redacted]';
        return;
      }
      if (typeof value === 'string' && isUrlFieldKey(key)) {
        scrubbed[key] = truncate(scrubUrlPath(value));
        return;
      }
      scrubbed[key] = scrub(value, depth + 1);
    });
    return scrubbed;
  }

  if (typeof data === 'string') {
    return truncate(looksLikeUrl(data) ? scrubUrlPath(data) : data);
  }

  if (typeof data === 'number' || typeof data === 'boolean' || data === null) {
    return data;
  }

  return '[redacted_non_scalar]';
}

export function isUrlFieldKey(key) {
  return URL_FIELD_KEYS.includes(String(key).toLowerCase());
}

export function looksLikeUrl(value) {
  return typeof value === 'string' && /^https?:\/\//i.test(value);
}

function normalizeKey(key) {
  return String(key).toLowerCase().replace(/[_-]/g, '');
}

function keyTokens(key) {
  const spaced = String(key).replace(/([a-z0-9])([A-Z])/g, '$1 $2');
  return spaced.toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
}

function fragmentMatchesKey(key, normalized, compact, fragment) {
  if (fragment === 'pan') {
    return keyTokens(key).includes('pan');
  }

  const compactFragment = normalizeKey(fragment);
  return normalized.includes(fragment) || compact.includes(compactFragment);
}

export function isSensitiveKey(key) {
  const normalized = String(key).toLowerCase();

  if (SENSITIVE_EXACT_KEYS.includes(normalized)) {
    return true;
  }

  const compact = normalizeKey(key);
  return SENSITIVE_KEY_FRAGMENTS.some((fragment) => fragmentMatchesKey(key, normalized, compact, fragment));
}

export function scrubMessage(message, category = null) {
  if (message == null) {
    return null;
  }

  const text = String(message);

  if (typeof category === 'string' && (category === 'console' || category.startsWith('console'))) {
    return '[redacted_console]';
  }

  const normalized = text.toLowerCase();
  if (RISKY_MESSAGE_PATTERNS.some((pattern) => normalized.includes(pattern.toLowerCase()))) {
    return '[redacted_message]';
  }

  if (looksLikeUrl(text)) {
    return truncate(scrubUrlPath(text));
  }

  if (/\d{7,}/.test(text)) {
    return truncate(text.replace(LONG_DIGIT_RUN, '[redacted_number]'));
  }

  if (typeof category === 'string' && category.startsWith('log.')) {
    return truncate(text, 120);
  }

  return truncate(text);
}

export function scrubExceptionMessage(message) {
  if (!message) {
    return message;
  }

  if (message.startsWith('HTTP/') || message.includes('\nHTTP/')) {
    return '[redacted_http_response]';
  }

  const normalized = message.toLowerCase();
  if (RISKY_MESSAGE_PATTERNS.some((pattern) => normalized.includes(pattern.toLowerCase()))) {
    return '[redacted_message]';
  }

  const withoutSql = message.replace(/\bSQL:\s.*$/s, 'SQL: [redacted]');
  return truncate(withoutSql.replace(LONG_DIGIT_RUN, '[redacted_number]'));
}

export function scrubUrlPath(url) {
  if (!url || typeof url !== 'string') {
    return url;
  }

  return url
    .replace(/#.*$/, '')
    .replace(/[?&].*$/, '')
    .replace(MOBILE_TOKEN_PATH, '/m/$1/[redacted]')
    .replace(/\/\d{7,}(?=[/?#]|$)/g, '/[redacted]');
}

export function truncate(value, max = 500) {
  if (typeof value !== 'string') {
    return value;
  }

  if (value.length <= max) {
    return value;
  }

  return `${value.slice(0, max)}…`;
}
